import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import CallerDashboard from "./pages/CallerDashboard";
import CustomerDashboard from "./pages/CustomerDashboard";
import UserManagement from "./pages/UserManagement";
import CallLogs from "./pages/CallLogs";
import Campaigns from "./pages/Campaigns";
import PaySheet from "./pages/PaySheet";
import DailyLeadCount from "./pages/DailyLeadCount";
import WaitingList from "./pages/WaitingList";
import CustomerMappings from "./pages/CustomerMappings";
import ResultOptions from "./pages/ResultOptions";
import AdminSettings from "./pages/AdminSettings";
import AdminChangeRequests from "./pages/AdminChangeRequests";
import Approvals from "./pages/Approvals";
import ChangeRequestHistory from "./pages/ChangeRequestHistory";
import AdminChat from "./pages/AdminChat";
import AdminSms from "./pages/AdminSms";
import AdminSmsSettings from "./pages/AdminSmsSettings";
import AdminChatSettings from "./pages/AdminChatSettings";
import CustomerChat from "./pages/CustomerChat";
import AdminFaq from "./pages/AdminFaq";
import CustomerFaq from "./pages/CustomerFaq";
import HowItWorks from "./pages/HowItWorks";
import ViewAs from "./pages/ViewAs";
import Eula from "./pages/Eula";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DashboardLayout from "./components/DashboardLayout";
import ErrorBoundary from "./components/ErrorBoundary";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user.role && !allowedRoles.includes(user.role)) {
    return <Navigate to={`/${user.role}`} replace />;
  }

  // If user exists but role is undefined, redirect to login
  if (!user.role) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user || !user.role) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={`/${user.role}`} replace />;
};

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: 'hsl(220, 15%, 13%)',
              color: 'hsl(220, 10%, 95%)',
              border: '1px solid hsl(220, 15%, 20%)',
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/view-as" element={<ViewAs />} />
          <Route path="/eula" element={<Eula />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/" element={<RoleBasedRedirect />} />
          
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <AdminDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <UserManagement />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/call-logs"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <CallLogs />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/campaigns"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <Campaigns />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pay-sheet"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <PaySheet />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/daily-leads"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <DailyLeadCount />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/waiting-list"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <WaitingList />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/customer-mappings"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <CustomerMappings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/result-options"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <ResultOptions />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <AdminSettings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/change-requests"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <AdminChangeRequests />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/approvals"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <Approvals />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/chat"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <AdminChat />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/chat/settings"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <AdminChatSettings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sms"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <AdminSms />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sms/settings"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <AdminSmsSettings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/faq"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <DashboardLayout>
                  <AdminFaq />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Caller Routes */}
          <Route
            path="/caller"
            element={
              <ProtectedRoute allowedRoles={["caller", "admin"]}>
                <DashboardLayout>
                  <CallerDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Customer Routes */}
          <Route
            path="/customer"
            element={
              <ProtectedRoute allowedRoles={["customer", "admin"]}>
                <DashboardLayout>
                  <CustomerDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/change-requests"
            element={
              <ProtectedRoute allowedRoles={["customer", "admin"]}>
                <DashboardLayout>
                  <ChangeRequestHistory />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/chat"
            element={
              <ProtectedRoute allowedRoles={["customer", "admin"]}>
                <DashboardLayout>
                  <CustomerChat />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/faq"
            element={
              <ProtectedRoute allowedRoles={["customer", "admin"]}>
                <DashboardLayout>
                  <CustomerFaq />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/how-it-works"
            element={
              <ProtectedRoute allowedRoles={["customer", "admin"]}>
                <DashboardLayout>
                  <HowItWorks />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
